"use client"; // Add this directive because we are using a hook

import React from "react";
import { usePathname, useSearchParams } from "next/navigation";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import {
    Box,
    Toolbar,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Avatar,
    Typography,
    Button,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDealership } from "@/contexts/DealershipContext";
import Cookies from "js-cookie";
import { AdminPermission, UIPermission } from "@/utils/permissions";
import SidebarLogo from "./branding/SidebarLogo";
import { useIsVariant, useVariant } from "@/hooks/useVariant";
import { usePageTitle } from "@/hooks/usePageTitle";

interface SidebarProps {
    drawerWidth: number;
}

export interface NavItem {
    text: string;
    path: string;
    className: string;
    permission: UIPermission | AdminPermission;
}

const Sidebar: React.FC<SidebarProps> = ({ drawerWidth }) => {
    const {
        filteredDealerReportingItems,
        filteredGroupReportingItems,
        filteredStandaloneItems,
        filteredMiddleStandaloneItems,
    } = useDealership();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const { userInfo, isUserInfoLoading } = useDealership();
    const { t } = useTranslation();

    const handleLogout = () => {
        Cookies.remove("esiq-nada-jwt");
        window.location.href = window.location.origin;
    };

    // Determine the page type based on the current pathname
    const getPageType = () => {
        if (pathname.startsWith("/group/")) return "group";
        // Check if it's a standalone page
        const isStandalonePage = filteredStandaloneItems.some((item) =>
            pathname.startsWith(item.path)
        );
        const isMiddleStandalonePage = filteredMiddleStandaloneItems.some((item) =>
            pathname.startsWith(item.path)
        );
        if (isStandalonePage || isMiddleStandalonePage) return "standalone";
        // Otherwise, it's a dealer page
        return "dealer";
    };

    const pageType = getPageType();

    // Get the active item based on the current page type
    const getActiveItem = () => {
        let itemsToCheck: NavItem[] = [];

        switch (pageType) {
            case "group":
                itemsToCheck = filteredGroupReportingItems;
                break;
            case "dealer":
                itemsToCheck = filteredDealerReportingItems;
                break;
            case "standalone":
                itemsToCheck = [...filteredStandaloneItems, ...filteredMiddleStandaloneItems];
                break;
        }

        return itemsToCheck.find((item) => pathname.startsWith(item.path))?.text || "";
    };

    const activeItem = getActiveItem();

    const isCada = useIsVariant("cada");

    // Browser-tab title: every authenticated page shows its nav label (e.g. "CADA: Compensation")
    // instead of the bare URL. The Sidebar renders only inside the authenticated layout and already
    // resolves the active nav item for the current route, so this covers all dashboard pages in one
    // place; public pages set their own titles via usePageTitle. [ES-1001]
    const variant = useVariant();
    const variantTitleKey =
        variant === "cada"
            ? "common.variants.cadaTitle"
            : variant === "atd"
              ? "common.variants.atdTitle"
              : "common.variants.nadaTitle";
    usePageTitle(activeItem ? t(activeItem) : t(variantTitleKey), variant);

    const renderNavItems = (items: NavItem[], pageType: string) => (
        <List disablePadding>
            {items.map((item) => (
                <ListItem
                    className={item.className}
                    sx={{ width: "100%" }}
                    key={item.text}
                    disablePadding
                >
                    <Link
                        href={{
                            pathname: item.path,
                            search: searchParams.toString(),
                        }}
                        passHref
                        style={{
                            textDecoration: "none",
                            color: "inherit",
                            width: "100%",
                            display: "block",
                        }}
                    >
                        <ListItemButton
                            sx={{
                                py: 0,
                                height: 48,
                                px: 2,
                                bgcolor:
                                    item.text === activeItem && getPageType() === pageType
                                        ? "#e9ecef"
                                        : "inherit",
                                "&:hover": {
                                    bgcolor: "#e9ecef",
                                },
                            }}
                        >
                            <ListItemText
                                primary={t(item.text)}
                                slotProps={{
                                    primary: {
                                        variant: "body1Medium",
                                    },
                                }}
                            />
                        </ListItemButton>
                    </Link>
                </ListItem>
            ))}
        </List>
    );

    const renderNavSection = (
        items: NavItem[],
        pageType: string,
        sectionTitle: string,
        showDivider: boolean = false
    ) => {
        if (items.length === 0) return null;

        return (
            <>
                <Accordion
                    defaultExpanded
                    sx={{
                        boxShadow: "none",
                        "&:before": { display: "none" },
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            px: 1,
                            minHeight: 48,
                            "&.Mui-expanded": {
                                minHeight: 48,
                            },
                            "& .MuiAccordionSummary-content": {
                                margin: "12px 0",
                                "&.Mui-expanded": {
                                    margin: "12px 0",
                                },
                            },
                        }}
                    >
                        <Typography variant="body1" fontWeight="medium">
                            {sectionTitle}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 1, pt: 0 }}>
                        {renderNavItems(items, pageType)}
                    </AccordionDetails>
                </Accordion>
                {showDivider && <Divider />}
            </>
        );
    };

    return (
        <Drawer
            variant="permanent"
            sx={{
                width: drawerWidth,
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: {
                    width: drawerWidth,
                    boxSizing: "border-box",
                    bgcolor: "white",
                    borderRight: "1px solid #dee2e6",
                },
            }}
        >
            <Toolbar
                sx={{
                    bgcolor: "white",
                    justifyContent: "left",
                    minHeight: "64px !important",
                    py: isCada ? 1 : 2,
                    paddingLeft: "12px !important",
                    paddingRight: "12px !important",
                }}
            >
                <SidebarLogo />
            </Toolbar>
            <Box sx={{ overflow: "auto", px: 0.5 }}>
                {renderNavSection(
                    filteredGroupReportingItems,
                    "group",
                    t("ui.navigation.sections.groupReporting"),
                    filteredMiddleStandaloneItems.length > 0 ||
                        filteredDealerReportingItems.length > 0 ||
                        filteredStandaloneItems.length > 0
                )}

                {filteredMiddleStandaloneItems.length > 0 && (
                    <Box sx={{ my: 0.5, p: 0 }}>
                        {renderNavItems(filteredMiddleStandaloneItems, "standalone")}
                    </Box>
                )}
                <Divider />

                {renderNavSection(
                    filteredDealerReportingItems,
                    "dealer",
                    t("ui.navigation.sections.dealerReporting"),
                    filteredStandaloneItems.length > 0
                )}

                {filteredStandaloneItems.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        {renderNavItems(filteredStandaloneItems, "standalone")}
                    </Box>
                )}
            </Box>
            <Box sx={{ mt: "auto", p: 3, bgcolor: "white" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                        sx={{
                            //   bgcolor: "#6a1b9a",
                            bgcolor: "#BDBDBD",
                            width: 40,
                            height: 40,
                            fontWeight: 500,
                            fontSize: 18,
                        }}
                    >
                        {userInfo?.first_name && userInfo?.last_name
                            ? `${userInfo.first_name[0]}${userInfo.last_name[0]}`
                            : ""}
                    </Avatar>
                    <Box>
                        <Typography variant="body2">
                            {isUserInfoLoading ? (
                                <CircularProgress />
                            ) : (
                                `${userInfo?.first_name} ${userInfo?.last_name}`
                            )}
                        </Typography>
                        <Button
                            size="small"
                            variant="text"
                            onClick={handleLogout}
                            sx={{
                                textTransform: "none",
                                p: 0,
                                minWidth: 0,
                                justifyContent: "flex-start",
                                "&:hover": { bgcolor: "transparent" },
                            }}
                        >
                            <Typography variant="body2" fontWeight="bold" color="primary">
                                {t("ui.navigation.logout")}
                            </Typography>
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
};

export default Sidebar;
